// File des notifications (toasts) : règles pures, sans React ni DOM, testées sous Node.
// Affichage et accessibilité : components/Toast.jsx.

export const MAX_TOASTS = 3;
export const TOAST_TONES = ['success', 'info', 'warning', 'error'];
// Durée d'affichage par défaut (ms) : une erreur reste nettement plus longtemps
export const DEFAULT_DURATIONS = { success: 2600, info: 4000, warning: 6000, error: 10000 };
// Toast avec action : le temps d'atteindre le bouton (souris, ou F8 au clavier)
export const ACTION_MIN_DURATION = 8000;
// Après une pause (survol, focus, onglet masqué) : au moins ce délai avant de disparaître
export const RESUME_MIN_MS = 1500;
// Texte des zones d'annonce vidé après ce délai (rien de périmé à relire au clavier)
export const ANNOUNCE_CLEAR_MS = 7000;

/** Erreur : annoncée tout de suite (assertive) ; le reste attend la fin de la lecture en cours. */
export const toastPoliteness = (tone) => (tone === 'error' ? 'assertive' : 'polite');

/**
 * Toast normalisé, compatible avec les appels existants toast(message, { tone }).
 * Options : tone (défaut « success »), duration en ms (Infinity : jusqu'à fermeture),
 * action { label, onClick } facultative. Message vide : null (rien à afficher).
 */
export function createToast(message, options, id) {
  const text = typeof message === 'string' ? message.trim() : message;
  if (text === null || text === undefined || text === '' || text === false) return null;
  const opts = options ?? {};
  const tone = TOAST_TONES.includes(opts.tone) ? opts.tone : 'success';
  const label = typeof opts.action?.label === 'string' ? opts.action.label.trim() : '';
  const action = label && typeof opts.action.onClick === 'function' ? { label, onClick: opts.action.onClick } : null;
  let duration = typeof opts.duration === 'number' && opts.duration > 0 ? opts.duration : DEFAULT_DURATIONS[tone];
  if (action) duration = Math.max(duration, ACTION_MIN_DURATION);
  return {
    id,
    message: text,
    tone,
    duration,
    action,
    // Bouton Fermer : erreurs, avertissements, toasts avec action ou sans fin
    dismissible: tone === 'error' || tone === 'warning' || !!action || duration === Infinity,
    politeness: toastPoliteness(tone),
  };
}

/**
 * Ajoute `toast` à la file (du plus ancien au plus récent). Même message et même ton déjà
 * affichés : ce toast est relancé, sans doublon. Au-delà de `max`, le plus ancien cède la
 * place, les erreurs en dernier ; jamais un toast de `keep` (survolé ou avec le focus).
 * Renvoie { list, id (toast affiché), removed (identifiants retirés) }.
 */
export function addToast(list, toast, { max = MAX_TOASTS, keep = [] } = {}) {
  const same = list.find((t) => t.message === toast.message && t.tone === toast.tone);
  if (same) {
    const refreshed = { ...toast, id: same.id };
    return { list: list.map((t) => (t.id === same.id ? refreshed : t)), id: same.id, removed: [] };
  }
  const kept = new Set(keep);
  let next = [...list, toast];
  const removed = [];
  while (next.length > max) {
    const candidates = next.filter((t) => t.id !== toast.id && !kept.has(t.id));
    const victim = candidates.find((t) => t.tone !== 'error') ?? candidates[0];
    if (!victim) break;
    removed.push(victim.id);
    next = next.filter((t) => t.id !== victim.id);
  }
  return { list: next, id: toast.id, removed };
}

export const removeToast = (list, id) => list.filter((t) => t.id !== id);

/** Texte annoncé aux lecteurs d'écran ; une action signale son raccourci (F8). */
export function announcement(toast) {
  const text = typeof toast.message === 'string' ? toast.message : '';
  if (!toast.action) return text;
  const sentence = /[.!?…]$/.test(text) ? text : `${text}.`;
  return `${sentence} Action disponible : ${toast.action.label} (touche F8).`;
}

/**
 * Minuteries des toasts. Suspendues tant qu'une raison de pause est active (« hover:3 »,
 * « focus », « hidden »…) ; à la reprise, chaque toast garde le temps qui lui restait,
 * au moins RESUME_MIN_MS. Un toast sans fin (Infinity) n'a pas de minuterie.
 * Horloge injectable (tests).
 */
export function createToastTimers({ onExpire, schedule = setTimeout, cancel = clearTimeout, now = Date.now }) {
  const entries = new Map(); // id → { remaining, startedAt, handle }
  const holds = new Set();

  const arm = (id, entry) => {
    entry.startedAt = now();
    entry.handle = schedule(() => {
      entries.delete(id);
      onExpire(id);
    }, entry.remaining);
  };
  const disarm = (entry) => {
    if (entry.handle === null) return;
    cancel(entry.handle);
    entry.handle = null;
    entry.remaining = Math.max(0, entry.remaining - (now() - entry.startedAt));
  };
  const clear = (id) => {
    const entry = entries.get(id);
    if (!entry) return;
    if (entry.handle !== null) cancel(entry.handle);
    entries.delete(id);
  };

  return {
    start(id, duration) {
      clear(id);
      if (!(duration > 0) || duration === Infinity) return;
      const entry = { remaining: duration, startedAt: 0, handle: null };
      entries.set(id, entry);
      if (!holds.size) arm(id, entry);
    },
    clear,
    clearAll() {
      entries.forEach((entry) => entry.handle !== null && cancel(entry.handle));
      entries.clear();
    },
    hold(reason) {
      if (holds.has(reason)) return;
      holds.add(reason);
      if (holds.size === 1) entries.forEach(disarm);
    },
    release(reason) {
      if (!holds.delete(reason) || holds.size) return;
      entries.forEach((entry, id) => {
        entry.remaining = Math.max(entry.remaining, RESUME_MIN_MS);
        arm(id, entry);
      });
    },
    isPaused: () => holds.size > 0,
    /** Temps restant (ms) avant la disparition de `id`, null sans minuterie. */
    remaining(id) {
      const entry = entries.get(id);
      if (!entry) return null;
      return entry.handle === null ? entry.remaining : Math.max(0, entry.remaining - (now() - entry.startedAt));
    },
  };
}
